import React from 'react';
import { Link } from 'react-router-dom';

import RecordBadge, { RecordTitle } from '../common/RecordBadge';
import { normalizeId } from '../../utils/ids';
import {
	RECORD_LIST_EMPTY_TEXT,
	pinAccessibleName,
	pinBadge,
	recordListKicker,
	recordListLabel,
	recordListPath,
	recordPath,
	recordTitle,
} from './homePins';
import { TILE_UNAVAILABLE_TEXT } from './RecordTile';

/**
 * A short list of records on Home's Pinned strip (KPR-1128): a cell two
 * columns wide (full width on a phone) that shows the first few records of a
 * card the person reads — newest first, or under one of the collection's
 * saved filters in its own order — as Home read them when it loaded. Its
 * heading is the card's record badge with the filter's name (else the card's
 * plural), and opens the whole list; each row is a record badge, one click to
 * that record where it lives now. No values: the rows are titles alone.
 *
 * With nothing matching it says so in one line, with the link to the list;
 * a list Home could not read this time (its filter no longer reads, the
 * search ran out of time) says what a record tile says then.
 *
 * `handle` is the reorder grip, `menu` its ⋯.
 */
export default function RecordList({ pin, handle = null, menu = null }) {
	const path = recordListPath(pin);
	const badge = pinBadge(pin);
	const label = recordListLabel(pin);
	const items = Array.isArray(pin?.items) ? pin.items : [];
	let body;
	if (pin?.unavailable) {
		body = <p className="home-list__none">{TILE_UNAVAILABLE_TEXT}</p>;
	} else if (!items.length) {
		body = (
			<p className="home-list__none">
				{RECORD_LIST_EMPTY_TEXT}
				{path ? <>{' · '}<Link to={path} className="home-list__open">Open the list</Link></> : null}
			</p>
		);
	} else {
		body = (
			<ul className="home-list__rows" aria-label={label}>
				{items.map(item => (
					<li key={normalizeId(item._id)} className="home-list__row">
						<RecordBadge
							kind="item"
							record={badge.record}
							title={recordTitle({ item })}
							size="xs"
							block
							to={recordPath({ item })}
							className="home-list__record"
						/>
					</li>
				))}
			</ul>
		);
	}
	return (
		<div className="home-list" data-pin-kind={pin?.targetType}>
			{handle}
			<section className="home-list__body" aria-label={pinAccessibleName(pin)}>
				{path ? (
					<Link to={path} className="home-list__head" aria-label={`Open the list: ${label}`}>
						<RecordTitle kind={badge.kind} record={badge.record} kicker={recordListKicker(pin)} title={label} size="xs" />
					</Link>
				) : (
					<span className="home-list__head">
						<RecordTitle kind={badge.kind} record={badge.record} kicker={recordListKicker(pin)} title={label} size="xs" />
					</span>
				)}
				{body}
			</section>
			{menu}
		</div>
	);
}
